import { DateTime } from 'luxon';

const filterEvents = (events, { searchInput, filters }) => {
  const { categories, dateRange, customDate, timeOfDay } = filters;
  const lowerSearch = searchInput.toLowerCase();

  return (
    events?.filter((event) => {
      const matchesSearch =
        !searchInput ||
        event.title?.toLowerCase().includes(lowerSearch) ||
        event.venue?.name?.toLowerCase().includes(lowerSearch) ||
        event.categories?.some((cat) =>
          cat.name?.toLowerCase().includes(lowerSearch)
        );

      const matchesCategory =
        categories.size === 0 ||
        event.categories?.some((cat) => categories.has(cat.name));

      const eventStartDate = DateTime.fromISO(
        event.schedules?.[0]?.startDate
      ).toJSDate();

      const matchesTime =
        timeOfDay.size === 0 ||
        (timeOfDay.has('morning') && getTimeOfDay(event) === 'morning') ||
        (timeOfDay.has('afternoon') && getTimeOfDay(event) === 'afternoon') ||
        (timeOfDay.has('evening') && getTimeOfDay(event) === 'evening');

      const matchesCustomDate =
        dateRange === 'custom'
          ? isEventInDateRange(event, {
              start: customDate.startDate,
              end: customDate.endDate,
            })
          : true;

      const matchesDate =
        !dateRange ||
        (dateRange === 'today' && isToday(eventStartDate)) ||
        (dateRange === 'week' && isThisWeek(eventStartDate)) ||
        (dateRange === 'custom' && matchesCustomDate);

      return matchesSearch && matchesCategory && matchesTime && matchesDate;
    }) || []
  );
};

const groupEventsByMonth = (events, hasActiveFilters) => {
  if (hasActiveFilters) {
    return [
      [
        'Filtered Results',
        events.sort((a, b) => {
          const dateA = DateTime.fromISO(a.schedules?.[0]?.startDate);
          const dateB = DateTime.fromISO(b.schedules?.[0]?.startDate);
          return dateA < dateB ? -1 : 1;
        }),
      ],
    ];
  }

  const grouped = events.reduce((acc, event) => {
    const startDate = DateTime.fromISO(event.schedules?.[0]?.startDate);
    if (!startDate.isValid) return acc;
    const key = startDate.toFormat('MMMM yyyy');
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(event);
    return acc;
  }, {});

  Object.keys(grouped).forEach((key) => {
    grouped[key].sort((a, b) => {
      const dateA = DateTime.fromISO(a.schedules?.[0]?.startDate);
      const dateB = DateTime.fromISO(b.schedules?.[0]?.startDate);
      return dateA < dateB ? -1 : 1;
    });
  });

  return Object.entries(grouped).sort(([a], [b]) => {
    const dateA = DateTime.fromFormat(a, 'MMMM yyyy');
    const dateB = DateTime.fromFormat(b, 'MMMM yyyy');
    return dateA < dateB ? -1 : 1;
  });
};

const hasActiveFilters = ({ searchInput, filters }) => {
  const { categories, dateRange, customDate, timeOfDay } = filters;
  const hasCustom = customDate.startDate && customDate.endDate;
  return (
    searchInput ||
    categories.size > 0 ||
    timeOfDay.size > 0 ||
    dateRange ||
    hasCustom
  );
};

// Helper functions

const isToday = (date) =>
  DateTime.fromJSDate(date).hasSame(DateTime.now(), 'day');

const isThisWeek = (date) => {
  const dt = DateTime.fromJSDate(date);
  const weekFromNow = DateTime.now().plus({ days: 7 });
  return dt <= weekFromNow;
};

const getTimeOfDay = (event) => {
  if (!event?.schedules?.[0]) return 'morning';
  const schedule = event.schedules[0];
  const firstOpenDay = schedule.weekdays.find((day) => !day.isClosed);
  if (!firstOpenDay) return 'morning';
  if (
    firstOpenDay.sessions &&
    Array.isArray(firstOpenDay.hours) &&
    firstOpenDay.hours.length > 0
  ) {
    const firstSession = firstOpenDay.hours[0];
    const [hour] = firstSession.split(':').map(Number);
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    return 'evening';
  }
  if (typeof firstOpenDay.openHour === 'number') {
    if (firstOpenDay.openHour < 12) return 'morning';
    if (firstOpenDay.openHour < 17) return 'afternoon';
    return 'evening';
  }
  return 'morning';
};

const isEventInDateRange = (event, { start, end }) => {
  const schedule = event.schedules?.[0];
  if (!schedule) return false;
  const eventStart = DateTime.fromISO(schedule.startDate);
  const eventEnd = DateTime.fromISO(schedule.endDate) || eventStart;
  if (!start || !end || !eventStart.isValid) return false;
  const rangeStart = DateTime.fromJSDate(start).startOf('day');
  const rangeEnd = DateTime.fromJSDate(end).startOf('day');
  const overlap = !(
    eventEnd.startOf('day') < rangeStart || eventStart.startOf('day') > rangeEnd
  );
  if (!overlap) return false;
  if (schedule.weekdays?.length) {
    let current = rangeStart;
    while (current <= rangeEnd) {
      const weekday = current.toFormat('EEE').toUpperCase();
      const scheduleDay = schedule.weekdays.find(
        (day) => day.weekday.toUpperCase() === weekday
      );
      if (scheduleDay && !scheduleDay.isClosed) return true;
      current = current.plus({ days: 1 });
    }
    return false;
  }
  return true;
};

export { filterEvents, groupEventsByMonth, hasActiveFilters };
